<template>
    <div
        v-if="visible"
        class="fixed bottom-24 left-4 right-4 z-[60] mx-auto max-h-[calc(100vh-8rem)] max-w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden rounded border border-brand-primary-200 bg-white shadow-lg dark:border-brand-primary-700 dark:bg-neutral-900 md:bottom-6 md:left-auto md:right-6 md:max-w-lg"
    >
        <div class="p-4">
            <div class="mb-3 flex items-start justify-between gap-3">
                <div class="min-w-0">
                    <div class="text-base font-semibold text-neutral-900 dark:text-white">
                        {{ copy.title }}
                    </div>
                    <div class="mt-1 text-sm leading-5 text-neutral-600 dark:text-neutral-300">
                        {{ copy.description }}
                    </div>
                </div>
                <button
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-xl leading-none text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-white"
                    type="button"
                    aria-label="Dismiss feedback"
                    @click="handleDismiss"
                >
                    &times;
                </button>
            </div>

            <div v-if="!submitted" class="space-y-3">
                <div>
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        Feedback type
                    </label>
                    <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
                        <button
                            v-for="option in feedbackTypeOptions"
                            :key="option.value"
                            class="flex items-center justify-center rounded border px-3 py-2 text-center text-sm font-semibold"
                            :class="feedbackType === option.value
                                ? 'border-brand-primary-500 bg-brand-primary-50 text-brand-primary-800 dark:bg-brand-primary-900/40 dark:text-brand-primary-100'
                                : 'border-neutral-300 text-neutral-700 hover:border-neutral-400 dark:border-neutral-700 dark:text-neutral-200 dark:hover:border-neutral-500'"
                            type="button"
                            @click="feedbackType = option.value"
                        >
                            <Icon fixed-width :icon="option.icon" />
                            <span class="ml-2">{{ option.label }}</span>
                        </button>
                    </div>
                </div>

                <div>
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        {{ copy.primaryLabel }}
                    </label>
                    <textarea
                        v-model="description"
                        class="min-h-[112px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                        maxlength="3000"
                        :placeholder="copy.primaryPlaceholder"
                        @focus="handleStart"
                    ></textarea>
                </div>

                <div>
                    <label class="mb-2 block text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                        {{ copy.secondaryLabel }}
                    </label>
                    <textarea
                        v-model="stepsToReproduce"
                        class="min-h-[88px] w-full rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none focus:border-brand-primary-500 dark:border-neutral-700 dark:bg-neutral-950 dark:text-white"
                        maxlength="3000"
                        :placeholder="copy.secondaryPlaceholder"
                        @focus="handleStart"
                    ></textarea>
                </div>

                <div v-if="error" class="text-sm text-red-600 dark:text-red-400">
                    {{ error }}
                </div>

                <div class="flex flex-wrap items-center justify-end gap-2">
                    <button
                        class="rounded px-3 py-2 text-sm font-semibold text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        type="button"
                        :disabled="submitting"
                        @click="handleDismiss"
                    >
                        Cancel
                    </button>
                    <button
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
                        type="button"
                        :disabled="submitting || !canSubmit"
                        @click="handleSubmit"
                    >
                        {{ submitting ? 'Sending...' : copy.submit }}
                    </button>
                </div>
            </div>

            <div v-else class="space-y-4">
                <div class="rounded bg-neutral-100 p-3 text-sm leading-5 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                    {{ copy.success }}
                </div>
                <div class="flex justify-end">
                    <button
                        class="rounded bg-brand-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-700"
                        type="button"
                        @click="visible = false"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {mapActions} from 'vuex';
import {
  capturePostHogEvent,
  getPostHogDistinctId,
} from '@/utils/posthog';

const promptLocation = 'account_menu';
const feedbackCopies = {
  bug_report: {
    description: 'Tell us what broke or felt wrong. We will attach your account and current page so we can investigate.',
    primaryLabel: 'What happened?',
    primaryPlaceholder: 'Describe the bug, what you expected, and anything that looked off.',
    secondaryLabel: 'Steps to reproduce',
    secondaryPlaceholder: 'Optional, but helpful: what did you tap or do right before it happened?',
    submit: 'Send Bug Report',
    success: 'Thanks. The bug report is saved with your account so we can investigate and follow up if needed.',
    title: 'Report Bug',
  },
  feature_feedback: {
    description: 'Tell us what you think about a feature, especially something new, confusing, missing, or surprisingly useful.',
    primaryLabel: 'What feedback do you have?',
    primaryPlaceholder: 'Example: The calendar is useful, but I wish saved workouts were easier to scan by template.',
    secondaryLabel: 'Where did this come up?',
    secondaryPlaceholder: 'Optional: name the feature, screen, workout flow, or recent release you are reacting to.',
    submit: 'Send Feature Feedback',
    success: 'Thanks. Your feature feedback is saved with your account so we can use it while improving V3ME.',
    title: 'Feature Feedback',
  },
  general_feedback: {
    description: 'Send any product feedback that does not quite fit as a bug report or a new idea.',
    primaryLabel: 'What would you like us to know?',
    primaryPlaceholder: 'Tell us what feels confusing, what is working well, or what would make V3ME more useful.',
    secondaryLabel: 'Extra context',
    secondaryPlaceholder: 'Optional: add anything about your workout setup, device, or goal that would help us understand.',
    submit: 'Send Feedback',
    success: 'Thanks. Your feedback is saved with your account so we can review it and follow up if needed.',
    title: 'Send Feedback',
  },
};

export default {
  name: 'FeedbackPrompt',
  data() {
    return {
      description: '',
      error: null,
      feedbackType: 'bug_report',
      feedbackTypeOptions: [
        {icon: 'bug', label: 'Bug', value: 'bug_report'},
        {icon: 'comment-lines', label: 'Feature', value: 'feature_feedback'},
        {icon: 'message-lines', label: 'General', value: 'general_feedback'},
      ],
      started: false,
      promptContext: {},
      promptLocation,
      stepsToReproduce: '',
      submitted: false,
      submitting: false,
      visible: false,
    };
  },
  computed: {
    canSubmit() {
      return this.description.trim().length >= 5;
    },
    copy() {
      return feedbackCopies[this.feedbackType] || feedbackCopies.general_feedback;
    },
    user() {
      return this.$store.getters['auth/user'];
    },
  },
  methods: {
    ...mapActions({
      storeBugReport: 'bugReport/store',
    }),
    eventProperties(extra = {}) {
      return {
        current_url: window.location.href,
        feedback_context: this.promptContext,
        feedback_type: this.feedbackType,
        prompt_location: this.promptLocation,
        user_workout_count: this.user?.workoutCount ?? null,
        ...extra,
      };
    },
    handleDismiss() {
      this.visible = false;
      capturePostHogEvent('feedback_prompt_dismissed', this.eventProperties());
    },
    handleOpen(event = {}) {
      const feedbackType = event?.detail?.feedbackType || 'bug_report';

      this.description = '';
      this.error = null;
      this.feedbackType = feedbackCopies[feedbackType] ? feedbackType : 'bug_report';
      this.promptContext = event?.detail?.context || {};
      this.promptLocation = event?.detail?.promptLocation || promptLocation;
      this.started = false;
      this.stepsToReproduce = '';
      this.submitted = false;
      this.submitting = false;
      this.visible = true;

      capturePostHogEvent('feedback_prompt_viewed', this.eventProperties());
    },
    handleStart() {
      if (this.started) {
        return;
      }

      this.started = true;
      capturePostHogEvent('feedback_started', this.eventProperties());
    },
    async handleSubmit() {
      if (!this.canSubmit || this.submitting) {
        return;
      }

      this.error = null;
      this.submitting = true;

      try {
        const response = await this.storeBugReport({
          currentUrl: window.location.href,
          description: this.description.trim(),
          feedbackType: this.feedbackType,
          metadata: {
            app_version: this.$store.state.system.settings?.version ?? null,
            feedback_context: this.promptContext,
            feedback_type: this.feedbackType,
            route: this.$route?.fullPath ?? null,
            viewport: {
              height: window.innerHeight,
              width: window.innerWidth,
            },
          },
          posthogDistinctId: getPostHogDistinctId(),
          promptLocation: this.promptLocation,
          stepsToReproduce: this.stepsToReproduce.trim() || null,
        });

        const report = response?.data?.data;

        this.submitted = true;
        capturePostHogEvent('feedback_submitted', this.eventProperties({
          bug_report_id: report?.id ?? null,
          has_steps_to_reproduce: this.stepsToReproduce.trim().length > 0,
        }));
      } catch (error) {
        this.error = 'We could not save that feedback. Please try again.';
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    window.addEventListener('v3me:feedback-prompt', this.handleOpen);
    window.addEventListener('v3me:bug-report-prompt', this.handleOpen);
  },
  beforeUnmount() {
    window.removeEventListener('v3me:feedback-prompt', this.handleOpen);
    window.removeEventListener('v3me:bug-report-prompt', this.handleOpen);
  },
};
</script>
